import React, { useState } from 'react'
import { Drawer, Tabs, Menu, Dropdown, Checkbox } from 'antd'
import Select from 'react-select'
import { useSnackbar } from 'notistack'
import { AiOutlineDown } from 'react-icons/ai'
import Creatable from 'react-select/creatable'

import { useToggle, useListData, usePet, notifOps } from '../../modules'

import Modal from '../reusable/modal'
import Confirm from '../reusable/confirm'
import SelectLabel from '../reusable/selectLabel'

import EditLinkedSaleItem from './editLinkedSaleItem'

const { TabPane } = Tabs

const ResolveConflicts = ({ visible, toggle, name, saleItems, onComplete }) => {
    const [items, setItems] = useState([])

    const toggleInclude = id => {
        let temp = [...items]
        if (temp.includes(id)) {
            temp = temp.filter((x) => x !== id)
        } else {
            temp.push(id)
        }
        setItems(temp)
    }    

    const [{ loading }, resolveConflicts] = usePet('resolveItemNameConflict', {
        name,
        items
    }, { notify: true, callback: (_, notif, ops) => {
        notif('Conflicts have been resolved', ops('success'))
        toggle()
        setTimeout(onComplete, 300)
    }, manual: true, load: { msg: 'Saving changes...' } })

    const save = () => Confirm({
        title: 'Save Changes?',
        message: 'This will rename the selected sale items',
        okText: 'Proceed',
        onOk: resolveConflicts
    })

    return (
        <Modal
            w={ 400 }
            title="Resolve Name Conflicts"
            toggle={toggle}
            visible={visible}>
            <p className="text-gray-500 text-xs italic">Checked items will be renamed</p>
            <table className="table-auto w-full mb-3">
                {/* Table header */}
                <thead className="text-xs uppercase text-gray-400 bg-gray-50 rounded-sm">
                    <tr>
                        <th className="p-2">
                            <div className="font-semibold text-center">Apply New</div>
                        </th>
                        <th className="p-2">
                            <div className="font-semibold text-left">Current Name</div>
                        </th>
                    </tr>
                </thead>
                <tbody className="text-sm font-medium divide-y divide-gray-100">
                    {
                        saleItems.map((itm, key) =>
                            <tr>
                                <td className="p-2 text-center">
                                    <Checkbox checked={ items.includes(itm?._id) } onChange={ () => toggleInclude(itm?._id) } />
                                </td>
                                <td className="p-2 text-left">{itm?.name}</td>
                            </tr>
                        )
                    }
                </tbody>
            </table>    
            <div className="flex justify-end">
                <button disabled={ loading } onClick={ save } className="text-white mr-2 hisani-btn-green font-medium rounded-lg text-sm px-5 py-2.5 text-center">Save Changes</button>
            </div>
        </Modal>
    )
}

const EditInventoryItem = ({ toggle, item: itm, store, visible, onPerform = () => null }) => {
    const [units, setUnits] = useState([])
    const [categories, setCategories] = useState([])

    const [name, setName] = useState(itm?.name)
    const [category, setCategory] = useState({ label: itm?.category, value: itm?.category })
    const [receivedIn, setReceivedIn] = useState({ label: itm?.receivedIn, value: itm?.receivedIn })
    const [issuedIn, setIssuedIn] = useState({ label: itm?.issuedIn, value: itm?.issuedIn })
    const [accounts, setAccounts] = useState([])
    const [saleItems, setSaleItems] = useState([])
    const [lists, setLists] = useState([])
    const [expense, setExpense] = useState()
    const [rl, setRl] = useState(0)
    const [tab, setTab] = useState("0")
    const [selected, setSelected] = useState()

    const { enqueueSnackbar } = useSnackbar()

    const [linked, toggleLinked] = useToggle()
    const [conflicts, toggleConflicts] = useToggle()

    usePet('inventoryCategories', null, { notify: true, callback: ({ data }) => setCategories(data) })
    usePet('inventoryUnits', null, { notify: true, callback: ({ data }) => setUnits(data) })

    const [, getSaleItems] = usePet('saleItemsByInventoryRef', { ref: itm._id }, { notify: true, callback: ({ data }) => {
        setSaleItems(data.map(si => ({
            ...si,
            account: { label: si.account.name, value: si.account._id }
        })))
    } })

    const onEditSaleItem = newName => {
        getSaleItems()

        if (newName !== name)
            Confirm({
                title: 'Inventory / Sale Item Name Conflict',
                message: 'Would you like to rename the inventory item to match the sale item?',
                okText: 'Proceed',
                onOk: () => {
                    setTab("0")
                    setName(newName)
                    enqueueSnackbar('Please remember to click the save button before exiting', notifOps('info'))
                },
                cancelText: 'Ignore Conflict'
            })
    }

    usePet('reorderLevelByStore', { item: itm._id, store: store._id }, { notify: true, callback: ({ data }) => setRl(data) })

    usePet('priceLists', { limit: 100 }, { notify: true, callback: async ({ data }) => {
        let final = []

        for (let { name, _id, defaultPricingType, defaultFormula } of data.lists)
            if (defaultPricingType)
                final.push({
                    name,
                    _id,
                    price: 0,
                    formula: defaultFormula,
                    type: defaultPricingType ?? "Direct Price"
                })

        setLists(final)
    } })

    useListData('accounts', { string: '', types: ['Income', 'Expenses', 'Cost of Goods Sold'] }, { notify: true }, 'accounts', 100, ({ accounts: data }) => {
        let obj = {}

        let expenseAcc = data.find(acc => acc._id === itm.account)
        if (expenseAcc) {
            setExpense({
                label: expenseAcc.name,
                value: expenseAcc._id
            })
        }

        data.forEach(acc => {
            if (!obj[acc.type])
                obj[acc.type] = []

            obj[acc.type].push({
                label: acc.name,
                value: acc._id
            })
        })

        let final = []
        for (let typ in obj) {
            final.push({
                label: typ,
                options: obj[typ]
            })
        }
        
        setAccounts(final)
    })

    const onComplete = ({ data }, notif, ops) => {
        onPerform(data)
        notif('Edit has been saved', ops('success'))

        if (saleItems.some(si => si.name !== name)) {
            Confirm({
                title: 'Inventory / Sale Item Name Conflict',
                message: 'Would you like to rename the linked sale items to match the inventory item?',
                okText: 'Proceed',
                onOk: toggleConflicts,
                cancelText: 'Ignore Conflict',
                onCancel: toggle
            })
        } else {
            setTimeout(toggle, 300)
        }
    }

    const [{ loading }, editInventoryItem] = usePet('editInventoryItem', {
        id: itm?._id,
        name,
        category: category?.label,
        receivedIn: receivedIn?.label,
        issuedIn: issuedIn?.label,
        store: store?.value ?? store?._id,
        reorderLevel: rl
    }, { notify: true, callback: onComplete, manual: true, load: { msg: 'Adding item...' } })

    let types = ['Direct Price', 'Computed Price']

    const close = () => {
        toggle()
    }

    const modify = e => {
        switch (e.target.name) {
            case 'name':
                setName(e.target.value)
                break

            case 'rl':
                setRl(e.target.value)
                break

            default:
                break
        }
    }

    const editSaleItem = si => {
        setSelected(si)
        toggleLinked()
    } 

    const save = () => Confirm({
        title: 'Save Edit?',
        message: 'Please confirm before proceeding?',
        okText: 'Proceed',
        onOk: editInventoryItem
    })

    return (
        <Drawer
            closable
            maskClosable
            visible={ visible }
            onClose={ close }
            footer={
                <div className="w-full text-right">
                    <button disabled={ loading } onClick={ save } type="submit" className="text-white hisani-btn-green-light font-medium rounded-lg text-sm px-5 py-2.5 text-center">Save</button>
                </div>
            }
            width="30%">
            <p className="font-extrabold text-3xl mb-0">Edit Inventory Item</p>
            <Tabs onChange={ setTab } activeKey={ tab }>
                <TabPane key="0" tab="Item Details">
                    <div className="flex flex-wrap mt-3">
                        <div className="w-full px-3 mb-1">
                            <label className="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2" htmlFor="grid-name">
                                Name
                            </label>
                            <input
                                className="appearance-none block w-full bg-gray-200 text-gray-700 border border-gray-200 rounded py-3 px-4 mb-3 leading-tight focus:outline-none focus:bg-white"
                                id="grid-name"
                                type="text"
                                name="name"
                                value={name}
                                onChange={modify}
                                placeholder="Potatoes" />
                        </div>
                        <div className="w-full px-3 mb-4">
                            <label className="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2" htmlFor="grid-name">
                                Category
                            </label>
                            <Creatable
                                value={category}
                                onCreateOption={val => {
                                    let temp = [...categories]
                                    temp.push(val)
                                    setCategories(temp)
                                    setCategory({ label: val, value: val })
                                }}
                                onChange={setCategory}
                                options={categories.map(category => ({ label: category, value: category }))}
                                placeholder="Select Category" />
                        </div>
                        <div className="w-full px-3 mb-4">
                            <label className="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2" htmlFor="grid-name">
                                Received In
                            </label>
                            <Creatable
                                value={receivedIn}
                                onCreateOption={val => {
                                    let temp = [...units]
                                    temp.push(val)
                                    setUnits(temp)
                                    setReceivedIn({ label: val, value: val })
                                }}
                                onChange={setReceivedIn}
                                options={units.map(unit => ({ label: unit, value: unit }))}
                                placeholder="Search for Unit" />
                        </div>
                        <div className="w-full px-3 mb-4">
                            <label className="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2" htmlFor="grid-name">
                                Issued In
                            </label>
                            <Creatable
                                value={issuedIn}
                                onCreateOption={val => {
                                    let temp = [...units]
                                    temp.push(val)
                                    setUnits(temp)
                                    setIssuedIn({ label: val, value: val })
                                }}
                                onChange={setIssuedIn}
                                options={units.map(unit => ({ label: unit, value: unit }))}
                                placeholder="Select Unit" />
                        </div>
                        <div className="w-full px-3 mb-4">
                            <label className="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2" htmlFor="grid-username">
                                Purchase Account
                            </label>
                            <Select
                                className="mb-3"
                                value={expense}
                                onChange={setExpense}
                                formatGroupLabel={SelectLabel}
                                options={ accounts.filter(acc => acc.label !== 'Income') }
                                placeholder="Select account" />
                        </div>
                        <div className="w-full px-3 mb-1">
                            <label className="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2" htmlFor="grid-name">
                                Reorder Level
                            </label>
                            <input
                                className="appearance-none block w-full bg-gray-200 text-gray-700 border border-gray-200 rounded py-3 px-4 mb-3 leading-tight focus:outline-none focus:bg-white"
                                id="grid-rl"
                                type="number"
                                name="rl"
                                onChange={modify}
                                value={rl}
                                placeholder="XXXX" />
                            <p className="text-gray-500 text-xs italic">This is the redorder level for { store?.name }</p>
                        </div>
                    </div>
                </TabPane>
                <TabPane key="1" tab="Sale Items">
                    <div className="font-bold mt-3">
                        {
                            saleItems.length > 0 ?
                                <table className="table-auto w-full mb-3">
                                    {/* Table header */}
                                    <thead className="text-xs uppercase text-gray-400 bg-gray-50 rounded-sm">
                                        <tr>
                                            <th className="p-2">
                                                <div className="font-semibold text-left">No</div>
                                            </th>
                                            <th className="p-2">
                                                <div className="font-semibold text-left">Name</div>
                                            </th>
                                            <th className="p-2">
                                                <div className="font-semibold text-left">Price</div>
                                            </th>
                                            <th className="p-2">
                                                <div className="font-semibold text-left">Actions</div>
                                            </th>
                                        </tr>
                                    </thead>
                                    {/* Table body */}
                                    <tbody className="text-sm font-medium divide-y divide-gray-100">
                                        {
                                            saleItems.map((si, key) =>
                                                <tr>
                                                    <td className="p-2 text-left">{ key + 1 }</td>
                                                    <td className="p-2 text-left">{ si?.name }</td>
                                                    <td className="p-2 text-left">{ si?.price }</td>
                                                    <td className="p-2 text-left">
                                                        <Dropdown
                                                            overlay={
                                                                <Menu>
                                                                    <Menu.Item onClick={ () => editSaleItem(si) }>View</Menu.Item>
                                                                </Menu>
                                                            }>
                                                            <a className="ant-dropdown-link flex items-center h-10" onClick={e => e.preventDefault()}><span className="mr-2">Actions</span> <AiOutlineDown /></a>
                                                        </Dropdown>
                                                    </td>
                                                </tr>
                                            )
                                        }
                                    </tbody>
                                </table>
                                :
                                <div className="text-center my-4">
                                    <h3>No sale items have been linked to this item</h3>
                                </div>
                        }
                    </div>
                </TabPane>
            </Tabs>
            { 
                linked &&
                <EditLinkedSaleItem
                    itm={ selected }
                    type="Sale Item"
                    priceLists={lists}
                    visible={ linked } 
                    accounts={ accounts }
                    onComplete={ onEditSaleItem }
                    toggle={ toggleLinked } />
            }
            {
                conflicts &&
                <ResolveConflicts
                    name={ name }
                    saleItems={saleItems}
                    visible={ conflicts }
                    toggle={ toggleConflicts }
                    onComplete={ toggle } />
            }
        </Drawer>
    )
}

export default EditInventoryItem